import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Globe, Instagram, MapPin, MessageCircle, Pencil } from "lucide-react";
import { format } from "date-fns";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UserAvatar } from "@/components/UserAvatar";
import { PostCard } from "@/components/PostCard";

const link = (url) => (url.startsWith("http") ? url : `https://${url}`);
const igLink = (h) => (h.startsWith("http") ? h : `https://instagram.com/${h.replace(/^@/, "")}`);

export default function MemberProfilePage() {
  const { memberId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [m, setM] = useState(null);
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    api.get(`/members/${memberId}`).then((r) => setM(r.data)).catch(() => navigate("/members"));
    api.get("/posts", { params: { author: memberId } }).then((r) => setPosts(r.data)).catch(() => {});
  }, [memberId, navigate]);

  if (!m) return <div className="text-[var(--hb-muted)]">Loading profile...</div>;
  const isMe = m.id === user.id;

  return (
    <div data-testid="member-profile-page">
      <Link to="/members" className="inline-flex items-center gap-2 text-sm text-[var(--hb-muted)] hover:text-[var(--hb-ink)]" data-testid="profile-back-link">
        <ArrowLeft size={15} /> Members
      </Link>
      <div className="grid lg:grid-cols-[320px_1fr] gap-8 mt-4">
        <aside className="hb-card p-6 text-center self-start rise">
          <div className="flex justify-center"><UserAvatar user={m} size={128} testId="member-profile-avatar" /></div>
          <h1 className="font-display text-3xl mt-4" data-testid="member-profile-name">{m.name}</h1>
          {(m.title || m.business) && <div className="mt-1">{[m.title, m.business].filter(Boolean).join(" · ")}</div>}
          {m.location && <div className="text-sm text-[var(--hb-muted)] mt-2 flex justify-center items-center gap-1"><MapPin size={13} />{m.location}</div>}
          {m.created_at && <div className="label-accent mt-3">Member since {format(new Date(m.created_at), "MMM yyyy")}</div>}
          <div className="flex flex-wrap justify-center gap-2 mt-5">
            {isMe ? (
              <Link to="/profile" className="btn-ghost" data-testid="member-profile-edit"><Pencil size={13} /> Edit profile</Link>
            ) : (
              <Link to={`/messages/${m.id}`} className="btn-primary" data-testid="member-profile-message"><MessageCircle size={14} /> Message</Link>
            )}
            {m.website && <a className="btn-ghost" href={link(m.website)} target="_blank" rel="noreferrer" data-testid="member-website-link"><Globe size={13} /> Website</a>}
            {m.instagram && <a className="btn-ghost" href={igLink(m.instagram)} target="_blank" rel="noreferrer" data-testid="member-instagram-link"><Instagram size={13} /> Instagram</a>}
          </div>
        </aside>
        <section className="space-y-5 min-w-0">
          <div className="hb-card p-6">
            <div className="label-accent">About</div>
            <p className="mt-3 whitespace-pre-wrap leading-relaxed" data-testid="member-profile-bio">
              {m.bio || <span className="text-[var(--hb-muted)]">{isMe ? "Add a bio from Edit profile." : "No bio yet."}</span>}
            </p>
          </div>
          <div className="label-accent pt-2">Posts by {m.name.split(" ")[0]}</div>
          {posts.length === 0 && <p className="text-sm text-[var(--hb-muted)]" data-testid="member-profile-no-posts">No posts yet.</p>}
          {posts.map((p) => (
            <PostCard key={p.id} post={p} onChange={(np) => setPosts(posts.map((x) => (x.id === np.id ? np : x)))}
              onDelete={(id) => setPosts(posts.filter((x) => x.id !== id))} />
          ))}
        </section>
      </div>
    </div>
  );
}
